import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import NavigationBar from '@/components/ui/NavigationBar';
import LoadingState from '@/components/ui/LoadingState';
import { LumoScoreCard, BetterThanYesterday } from '@/components/LumoCoachingPanel';
import { supabase } from '@/lib/supabase';
import Icon from '@/components/AppIcon';

export default function LumoProgress() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [dateRange, setDateRange] = useState('week'); // week, month, all
  const [avgScores, setAvgScores] = useState(null);
  const [improvementStreak, setImprovementStreak] = useState(0);
  const [mostImproved, setMostImproved] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      loadLumoProgress();
    }
  }, [user, dateRange]);

  const loadLumoProgress = async () => {
    setIsLoading(true);
    try {
      // Calculate date threshold
      const now = new Date();
      let dateThreshold = new Date();
      if (dateRange === 'week') {
        dateThreshold.setDate(now.getDate() - 7);
      } else if (dateRange === 'month') {
        dateThreshold.setDate(now.getDate() - 30);
      } else {
        dateThreshold = new Date('2024-01-01'); // All time
      }

      // Fetch objection stacks with Lumo scores
      const { data, error } = await supabase
        .from('objection_stacks')
        .select('*')
        .eq('user_id', user.id)
        .not('tone_consistency_score', 'is', null)
        .gte('created_at', dateThreshold.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      setSessions(data || []);

      // Calculate averages
      if (data && data.length > 0) {
        const avgTone = data.reduce((sum, s) => sum + (s.tone_consistency_score || 0), 0) / data.length;
        const avgProbe = data.reduce((sum, s) => sum + (s.probe_sharpness_score || 0), 0) / data.length;
        const avgDetachment = data.reduce((sum, s) => sum + (s.detachment_score || 0), 0) / data.length;
        const avgOverall = (avgTone + avgProbe + avgDetachment) / 3;

        setAvgScores({
          toneConsistency: parseFloat(avgTone.toFixed(2)),
          probeSharpness: parseFloat(avgProbe.toFixed(2)),
          detachment: parseFloat(avgDetachment.toFixed(2)),
          overall: parseFloat(avgOverall.toFixed(2))
        });

        // Calculate improvement streak
        let streak = 0;
        for (let i = 0; i < data.length - 1; i++) {
          const current = data[i].overall_handling_score || 0;
          const previous = data[i + 1].overall_handling_score || 0;
          if (current >= previous) {
            streak++;
          } else {
            break;
          }
        }
        setImprovementStreak(streak);

        // Find most improved metric
        if (data.length >= 5) {
          const recent = data.slice(0, 5);
          const older = data.slice(5, 10);
          
          if (older.length > 0) {
            const recentAvgTone = recent.reduce((sum, s) => sum + (s.tone_consistency_score || 0), 0) / recent.length;
            const recentAvgProbe = recent.reduce((sum, s) => sum + (s.probe_sharpness_score || 0), 0) / recent.length;
            const recentAvgDetach = recent.reduce((sum, s) => sum + (s.detachment_score || 0), 0) / recent.length;
            
            const olderAvgTone = older.reduce((sum, s) => sum + (s.tone_consistency_score || 0), 0) / older.length;
            const olderAvgProbe = older.reduce((sum, s) => sum + (s.probe_sharpness_score || 0), 0) / older.length;
            const olderAvgDetach = older.reduce((sum, s) => sum + (s.detachment_score || 0), 0) / older.length;
            
            const toneImprovement = ((recentAvgTone - olderAvgTone) / olderAvgTone) * 100;
            const probeImprovement = ((recentAvgProbe - olderAvgProbe) / olderAvgProbe) * 100;
            const detachImprovement = ((recentAvgDetach - olderAvgDetach) / olderAvgDetach) * 100;
            
            const improvements = [
              { metric: 'Tone Consistency', delta: toneImprovement },
              { metric: 'Probe Sharpness', delta: probeImprovement },
              { metric: 'Detachment', delta: detachImprovement }
            ];
            
            const best = improvements.reduce((max, curr) => curr.delta > max.delta ? curr : max);
            setMostImproved(best);
          }
        }
      }
    } catch (error) {
      console.error('Error loading Lumo progress:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading || isLoading) {
    return <LoadingState message="Loading your Lumo progress..." />;
  }

  return (
    <>
      <NavigationBar /><div className="main-content bg-background">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Lumo Progress</h1>
              <p className="text-muted-foreground mt-1">
                Track your Lumo Strategy mastery over time
              </p>
            </div>
            
            {/* Date Range Selector */}
            <div className="flex gap-2">
              {['week', 'month', 'all'].map((range) => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    dateRange === range
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card border border-border text-foreground hover:bg-muted'
                  }`}
                >
                  {range === 'week' ? 'Last 7 Days' : range === 'month' ? 'Last 30 Days' : 'All Time'}
                </button>
              ))}
            </div>
          </div>

          {/* No Data State */}
          {sessions.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl p-12 text-center">
              <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No Lumo Data Yet
              </h3>
              <p className="text-muted-foreground mb-6">
                Complete daily drills or AI roleplay sessions to start tracking your Lumo Strategy progress
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => navigate('/app/daily-drill')}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90"
                >
                  Start Daily Drill
                </button>
                <button
                  onClick={() => navigate('/app/ai-roleplay')}
                  className="px-6 py-3 bg-card border border-border text-foreground rounded-lg font-medium hover:bg-muted"
                >
                  Practice AI Roleplay
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Average Scores Card */}
              {avgScores && (
                <LumoScoreCard scores={avgScores} />
              )}

              {/* Streak & Most Improved */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Improvement Streak */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="TrendingUp" size={24} className="text-green-600" />
                    <h3 className="text-lg font-semibold text-green-900">
                      Better Than Yesterday Streak
                    </h3>
                  </div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-bold text-green-600">{improvementStreak}</span>
                    <span className="text-green-700">consecutive session{improvementStreak !== 1 ? 's' : ''}</span>
                  </div>
                  <p className="text-sm text-green-700">
                    {improvementStreak > 0 
                      ? 'Keep the momentum — you\'re wiring in the muscle memory' 
                      : 'Start your improvement streak with your next session'}
                  </p>
                </div>

                {/* Most Improved Metric */}
                {mostImproved && (
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Icon name="Award" size={24} className="text-purple-600" />
                      <h3 className="text-lg font-semibold text-purple-900">
                        Most Improved
                      </h3>
                    </div>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-4xl font-bold text-purple-600">
                        {mostImproved.delta > 0 ? '+' : ''}{mostImproved.delta.toFixed(1)}%
                      </span>
                      <span className="text-purple-700">{mostImproved.metric}</span>
                    </div>
                    <p className="text-sm text-purple-700">
                      Recent 5 sessions vs. previous 5 sessions
                    </p>
                  </div>
                )}
              </div>

              {/* Session History */}
              <div className="bg-card border border-border rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-foreground mb-6">Session History</h3>
                <div className="space-y-4">
                  {sessions.slice(0, 10).map((session, idx) => {
                    const overall = ((session.tone_consistency_score || 0) + (session.probe_sharpness_score || 0) + (session.detachment_score || 0)) / 3;
                    const prevScore = idx < sessions.length - 1 ? ((sessions[idx + 1].tone_consistency_score || 0) + (sessions[idx + 1].probe_sharpness_score || 0) + (sessions[idx + 1].detachment_score || 0)) / 3 : null;
                    const delta = prevScore ? ((overall - prevScore) / prevScore) * 100 : null;

                    return (
                      <div key={session.id} className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-sm font-semibold text-foreground">
                              {new Date(session.created_at).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                            <span className="text-xs text-muted-foreground capitalize">
                              {session.session_type?.replace('_', ' ')}
                            </span>
                            {session.time_to_resolve && (
                              <span className="text-xs text-muted-foreground">
                                {session.time_to_resolve}s
                              </span>
                            )}
                          </div>
                          <div className="flex gap-4 text-xs">
                            <span className="text-muted-foreground">
                              Tone: <strong className="text-foreground">{(session.tone_consistency_score * 100).toFixed(0)}%</strong>
                            </span>
                            <span className="text-muted-foreground">
                              Probe: <strong className="text-foreground">{(session.probe_sharpness_score * 100).toFixed(0)}%</strong>
                            </span>
                            <span className="text-muted-foreground">
                              Detach: <strong className="text-foreground">{(session.detachment_score * 100).toFixed(0)}%</strong>
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary">
                            {(overall * 100).toFixed(0)}
                          </div>
                          {delta !== null && (
                            <div className={`text-xs font-semibold ${delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                              {delta > 0 ? '↑' : delta < 0 ? '↓' : '→'} {Math.abs(delta).toFixed(1)}%
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tips */}
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <Icon name="Lightbulb" size={16} className="text-blue-600" />
                  Lumo Strategy Tips
                </h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li>• <strong>Tone Consistency:</strong> Avoid "I totally understand", "trust me" — stay neutral</li>
                  <li>• <strong>Probe Sharpness:</strong> Keep questions under 60 chars — one sharp question beats 3 statements</li>
                  <li>• <strong>Detachment:</strong> Use exit ramps like "Totally fine if this isn't a fit" — don't chase</li>
                  <li>• <strong>Better Than Yesterday:</strong> Focus on incremental progress, not perfection</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
